import { useEffect, useState } from "react";
import { ALIRecordRequest } from "services/ALIRecord";
import { Msag } from "services/MSAGRecord";
import { Input } from "./Input";

export function HouseNumberInput({
  recordRequest,
  setRecordRequest,
  lastMSAGRecord,
}: {
  recordRequest: ALIRecordRequest;
  setRecordRequest: React.Dispatch<React.SetStateAction<ALIRecordRequest>>;
  lastMSAGRecord: Msag | null;
}) {
  const [houseNumberString, setHouseNumberString] = useState<string>("");

  useEffect(() => {
    if (
      recordRequest.STA !== "" &&
      recordRequest.MCN !== "" &&
      recordRequest.STN !== "" &&
      recordRequest.STS !== ""
    ) {
      setHouseNumberString("");

      if (lastMSAGRecord && Array.isArray(lastMSAGRecord)) {
        const uniqueRanges = Array.from(
          new Set(
            lastMSAGRecord.map(
              (record: Msag) =>
                `${record.LowRange || "0"}-${record.HiRange || "0"}`,
            ),
          ),
        );
        const rangeStrings = "Ranges: " + uniqueRanges.join(", ");
        setHouseNumberString(rangeStrings);
      } else if (lastMSAGRecord && !Array.isArray(lastMSAGRecord)) {
        const houseNumberRange = `Ranges: ${lastMSAGRecord.LowRange || "0"}-${
          lastMSAGRecord.HiRange || "0"
        }`;
        setHouseNumberString(houseNumberRange);
      } else {
        setHouseNumberString(
          "No MSAG records ranges found for this selection.",
        );
      }
    }
  }, [
    lastMSAGRecord,
    recordRequest.CPN,
    recordRequest.HNO,
    recordRequest.MCN,
    recordRequest.STA,
    recordRequest.STN,
    recordRequest.STS,
  ]);

  return (
    <Input
      name="HNO"
      label="House Number*"
      value={recordRequest.HNO}
      onChange={(e) =>
        setRecordRequest((prev: ALIRecordRequest) => ({
          ...prev,
          HNO: e.target.value,
        }))
      }
      scale="s"
      labelSize="s"
      max={10}
      showMessageOnFocus
      message={houseNumberString ? houseNumberString : undefined}
      onKeyDown={(e) => {
        // if the user presses up go forwards through the house string
        // if the user presses down go backwards through the house string
        // go through every range from the housenumber string
        // if the corresponding side is 1 that means every odd number in that range is valid
        // if the corresponding side is 2 that means every even number in that range is valid
        // if the corresponding side is 3 that means every number in that range is valid

        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          const ranges = houseNumberString
            .replace("Ranges: ", "")
            .split(", ")
            .map((range) => {
              const match = range.match(/(\d+)(-(\d+))?([OEB])?/);
              if (match) {
                return {
                  low: parseInt(match[1], 10),
                  high: match[3]
                    ? parseInt(match[3], 10)
                    : parseInt(match[1], 10),
                  side: match[4] || "3",
                };
              }
              return null;
            })
            .filter((range) => range !== null) as {
            low: number;
            high: number;
            side: string;
          }[];

          if (ranges.length === 0) return;

          const currentHNO = recordRequest.HNO
            ? parseInt(recordRequest.HNO, 10)
            : NaN;
          let newHNO: number;

          const isValidForSide = (num: number, side: string): boolean => {
            if (side === "1" || side === "O") return num % 2 === 1; // Odd only
            if (side === "2" || side === "E") return num % 2 === 0; // Even only
            return true; // Both/Any (side === "3" || side === "B")
          };

          const getNextValidNumber = (
            start: number,
            range: { low: number; high: number; side: string },
            direction: 1 | -1,
          ): number => {
            let candidate = start;
            while (candidate >= range.low && candidate <= range.high) {
              if (isValidForSide(candidate, range.side)) {
                return candidate;
              }
              candidate += direction;
            }
            return -1; // No valid number found in range
          };

          // If HNO is empty or invalid, start from the first valid number in the first range
          if (isNaN(currentHNO)) {
            const firstRange = ranges[0];
            newHNO = getNextValidNumber(firstRange.low, firstRange, 1);
            if (newHNO === -1) {
              // If no valid number starting from low, try to find any valid number in range
              for (let i = firstRange.low; i <= firstRange.high; i++) {
                if (isValidForSide(i, firstRange.side)) {
                  newHNO = i;
                  break;
                }
              }
              if (newHNO === undefined) newHNO = firstRange.low; // fallback
            }
          } else {
            let found = false;
            newHNO = currentHNO;

            // Find current range and calculate next valid number
            for (const range of ranges) {
              if (currentHNO >= range.low && currentHNO <= range.high) {
                const direction = e.key === "ArrowUp" ? 1 : -1;
                const nextCandidate = getNextValidNumber(
                  currentHNO + direction,
                  range,
                  direction,
                );

                if (nextCandidate !== -1) {
                  newHNO = nextCandidate;
                  found = true;
                  break;
                }
              }
            }

            // If not found in current range or no valid number in current range
            if (!found) {
              if (e.key === "ArrowUp") {
                // Find next range with higher numbers
                const nextRange = ranges.find((r) => r.low > currentHNO);
                if (nextRange) {
                  const validStart = getNextValidNumber(
                    nextRange.low,
                    nextRange,
                    1,
                  );
                  if (validStart !== -1) {
                    newHNO = validStart;
                  } else {
                    // Find first valid number in range
                    for (let i = nextRange.low; i <= nextRange.high; i++) {
                      if (isValidForSide(i, nextRange.side)) {
                        newHNO = i;
                        break;
                      }
                    }
                  }
                }
              } else {
                // Find previous range with lower numbers
                const prevRanges = ranges.filter((r) => r.high < currentHNO);
                const prevRange = prevRanges[prevRanges.length - 1];
                if (prevRange) {
                  const validEnd = getNextValidNumber(
                    prevRange.high,
                    prevRange,
                    -1,
                  );
                  if (validEnd !== -1) {
                    newHNO = validEnd;
                  } else {
                    // Find last valid number in range
                    for (let i = prevRange.high; i >= prevRange.low; i--) {
                      if (isValidForSide(i, prevRange.side)) {
                        newHNO = i;
                        break;
                      }
                    }
                  }
                }
              }
            }
          }

          setRecordRequest((prev: ALIRecordRequest) => ({
            ...prev,
            HNO: newHNO.toString(),
          }));
        }
      }}
    />
  );
}
